import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FilesState, HandleUploadImgResponse } from 'src/AppState/FilesState/FilesState';
import { ChangeEvent } from 'react';

export class UploadImageState {
    @observable private imgErrorMessageInner: string | null = null;
    @observable private imgWarningMessageInner: string | null = null;
    @observable private onGoingUploadFile: File | null = null;
    @observable private imgUrlInner: string = '';
    @observable private isVisited: boolean = false;

    public filesState: FilesState;

    public constructor(
        private readonly directory: string,
        private readonly wantedFormat?: string
    ) {
        this.filesState = new FilesState(directory);
        makeAutoObservable(this);
    }

    @computed public get imgUrl(): string {
        return this.imgUrlInner;
    }

    @computed public get isValid(): boolean {
        return this.imgUrl !== '';
    }

    @computed public get isEmptyError(): boolean {
        return this.imgUrl === '' && this.isVisited;
    }

    @computed public get imgErrorMessage(): string | null {
        return this.imgErrorMessageInner;
    }

    @computed public get imgWarningMessage(): string | null {
        return this.imgWarningMessageInner;
    }

    @action public handleGetFilesList = async (): Promise<void> => {
        await this.filesState.onShowLibraryPopUp();
    };

    @action public handleDeleteSelectedImage = (): void => {
        this.imgUrlInner = '';
    };

    @action public handleCheckFormat = (
        event: ChangeEvent<HTMLInputElement>,
        callBack: (event: ChangeEvent<HTMLInputElement>) => void
    ): void => {
        const file = event.target.files?.[0] ?? null;

        if (
            file !== null &&
            file.type !== 'image/gif' &&
            file.type !== 'image/jpeg' &&
            file.type !== 'image/png' &&
            file.type !== 'image/svg+xml' &&
            file.type !== 'image/webp'
        ) {
            this.imgErrorMessageInner = 'Files with the following extensions are accepted: gif, jpg, png, svg, webp.';
            event.target.value = '';
        } else {
            callBack(event);
        }
    };

    @action public handleUploadAndChooseImage = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        this.imgErrorMessageInner = null;

        if (e.target.files !== null) {
            const file = e.target.files[0] ?? null;

            if (file !== null) {
                this.onGoingUploadFile = file;
                const response = await this.filesState.handleUploadImage(file, this.directory, this.wantedFormat);
                this.handleImgUploadResponse(response);
            }
        }
    };

    @action public forceUpload = async (): Promise<void> => {
        if (this.onGoingUploadFile !== null) {
            const response = await this.filesState.handleForceUploadImage(
                this.onGoingUploadFile,
                this.directory,
                this.wantedFormat
            );
            this.handleImgUploadResponse(response);
        }
        this.onGoingUploadFile = null;
        this.imgWarningMessageInner = null;
        this.imgErrorMessageInner = null;
    };

    @action public setIsVisited = (): void => {
        this.isVisited = true;
    };

    @action public hideSizeMessage = (): void => {
        this.imgWarningMessageInner = null;
        this.onGoingUploadFile = null;
    };

    @action private handleImgUploadResponse = (response: HandleUploadImgResponse): void => {
        if (response.status === 'error') {
            this.imgErrorMessageInner = response.message ?? null;
            return;
        }
        if (response.status === 'warning') {
            this.imgWarningMessageInner = response.message;
            return;
        }

        this.onGoingUploadFile = null;
        this.imgErrorMessageInner = null;
        this.imgWarningMessageInner = null;

        const img_url = response.data.img_urls[0] ?? null;
        if (img_url !== null) {
            this.imgUrlInner = img_url;
        }
    };

    @action public handleChooseImage = (img_url: string): void => {
        this.imgUrlInner = img_url;
    };

    @action public handleResetImage = (): void => {
        this.imgUrlInner = '';
        this.isVisited = false;
    };

    @action public handleDeleteImage = async (id: string): Promise<void> => {
        const fileDirectory = `${this.directory}/${id}`;
        await this.filesState.handleDeleteImage(fileDirectory);
    };
}
